/**
 * Guard on the brain boundary. Every decision is checked before the sim acts on it: only the
 * intents that make sense for the trigger, only radio kinds the log knows, and only values the
 * situation supports. A decision that fails is replaced by the rules brain's, and the reason is
 * reported so it can be counted.
 */
import { NEEDS, type RequestKind } from '../patience';
import { clarificationFor } from '../pilot-brain';
import { checkParts, type AllowedValues } from './phrase';
import type { PilotDecision, PilotIntent, PilotTrigger } from './types';

const ALLOWED_INTENTS: Record<PilotTrigger['kind'], readonly PilotIntent['kind'][]> = {
  checkin: ['say', 'request', 'silence'],
  event: ['say', 'request', 'silence'],
  initiative: ['say', 'request', 'silence'],
  patience: ['say', 'act', 'silence'],
  readback: ['say', 'silence'],
  unable: ['say', 'silence'],
  answer: ['say', 'silence'],
  conversation: ['say', 'silence'],
  reply: ['say', 'silence'],
  fuel: ['say', 'request', 'silence'],
  emergency: ['say', 'request', 'silence'],
};
const RADIO_KINDS = ['command', 'readback', 'unable', 'checkin', 'report', 'handoff', 'system', 'emergency'];
const MAX_INTENTS = 6;
/** Values a crew only speaks when it is reading back or reporting a clearance the sim executed. */
const CLEARANCE_PARTS = ['altitude', 'heading', 'speed'];

export type DecisionCheck = { ok: true } | { ok: false; reason: string };

export function validateDecision(decision: PilotDecision, trigger: PilotTrigger, allowed: AllowedValues = {}): DecisionCheck {
  if (!decision || !Array.isArray(decision.intents)) return { ok: false, reason: 'no intents' };
  if (decision.intents.length > MAX_INTENTS) return { ok: false, reason: `too many intents (${decision.intents.length})` };
  const permitted = ALLOWED_INTENTS[trigger.kind];
  if (trigger.kind === 'event' && trigger.event === 'requestClimb' && (!decision.intents.some((i) => i.kind === 'request' && i.request === 'climb') || !decision.intents.some((i) => i.kind === 'say'))) {
    return { ok: false, reason: 'airborne departure must request a climb clearance' };
  }
  const model = decision.source !== 'rules' && decision.source !== 'fallback';
  // Chat never changes the flight, so a model must not make it sound as if it did: no readback,
  // no quoted clearance values, and no answer at all where the crew has to ask what was meant.
  if (model && trigger.kind === 'conversation' && clarificationFor(trigger.topic, trigger.text)) {
    return { ok: false, reason: 'an instruction that was not executed needs the rules clarification' };
  }
  for (const intent of decision.intents) {
    if (model && (trigger.kind === 'conversation' || trigger.kind === 'reply') && intent?.kind === 'say') {
      if (intent.radio === 'readback') return { ok: false, reason: `a ${trigger.kind} reply is not a readback` };
      if ((intent.parts ?? []).some((p) => CLEARANCE_PARTS.includes(p?.t))) return { ok: false, reason: `a ${trigger.kind} reply may not quote clearance values` };
    }
    if (!intent || !permitted.includes(intent.kind)) return { ok: false, reason: `intent ${intent?.kind} is not allowed for ${trigger.kind}` };
    if (intent.kind === 'say') {
      if (!RADIO_KINDS.includes(intent.radio)) return { ok: false, reason: `unknown radio kind ${intent.radio}` };
      // The sim decided *why* the instruction is refused; a model may not reword the reason, or
      // pilots start saying things like "unable, heading not available" that no rule produced.
      if (intent.radio === 'unable' && decision.source !== 'rules' && decision.source !== 'fallback') {
        return { ok: false, reason: 'only the rules brain may word an unable' };
      }
      if (intent.phrase) {
        // The sim's own rendered phraseology; only the in-process rules brain may use it.
        if (decision.source !== 'rules' && decision.source !== 'fallback') return { ok: false, reason: 'only the rules brain may speak a rendered phrase' };
        if (typeof intent.phrase.text !== 'string' || typeof intent.phrase.speech !== 'string') return { ok: false, reason: 'phrase needs text and speech' };
        continue;
      }
      if (intent.form !== 'pilot' && intent.form !== 'plain') return { ok: false, reason: `unknown phrase form ${intent.form}` };
      const bad = checkParts(intent.parts ?? [], allowed);
      if (bad) return { ok: false, reason: bad };
      const words = (intent.parts ?? []).filter((p) => p.t === 'words').map((p) => p.t === 'words' ? p.v : '').join(' ');
      if (/\bin sight\b|\bvisual contact\b/i.test(words)) return { ok: false, reason: 'visual acquisition has not been modeled' };
    } else if (intent.kind === 'request') {
      if (!(intent.request in NEEDS)) return { ok: false, reason: `unknown request ${intent.request as RequestKind}` };
    } else if (intent.kind === 'act') {
      if (intent.action !== 'hold' && intent.action !== 'returnToStand') return { ok: false, reason: `unknown action ${intent.action}` };
    }
  }
  return { ok: true };
}
